import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  normalizeVehicleType,
  type LiveVehicle,
  type VehicleState,
  type VehicleType,
} from '@raad/live-markers';

import { ApiError, apiJson } from '@/lib/apiClient';

/**
 * The global live map's data (slice 17d; parity fields slice 84).
 *
 * Types mirror the wire contract in `api/src/modules/admin/liveRoutes.ts`
 * (seam S1) and are never re-declared from the screen's point of view.
 */

export type Motion = 'moving' | 'idle' | 'stopped' | 'unknown' | 'offline';

export interface AdminLiveVehicle {
  vehicleId: string;
  label: string;
  plate: string;
  tenantId: string;
  tenantName: string;
  tenantStatus: 'active' | 'past_due' | 'suspended';
  /**
   * FALSE means the CUSTOMER cannot see this vehicle — the go-live gate.
   * 🔴 The server derives it from the FULL predicate (`liveAt !== null &&
   * hiddenAt === null`), so a withdrawn vehicle is false here. It read `liveAt`
   * alone until fix/unhide-reachability.
   */
  live: boolean;
  /**
   * 🔴 THE EVIDENCE BEHIND `live`, and the screen needs it because `live: false`
   * covers TWO different situations that get two different panels: never
   * released (checks + Go live) and released-then-withdrawn (the withdrawn
   * banner, the dates, and Show again). `liveAt` is history and is never cleared
   * on withdrawal, which is exactly why it cannot answer visibility alone.
   */
  liveAt: string | null;
  /** Non-null = cleared once and since withdrawn from the customer. */
  hiddenAt: string | null;
  lat: number | null;
  lon: number | null;
  speedKmh: number | null;
  course: number | null;
  motion: Motion;
  fixTime: string | null;
  /**
   * 🔴 SLICE 84 — the parity fields the backend adds in parallel so the staff map
   * draws the SAME silhouettes (shape + state colour + heading) as the tenant map.
   * Optional on the wire so a response that predates them still parses (and the
   * mapper softens each): a missing `vehicleType` → the dot, a missing `state` →
   * `unknown`, missing `speeding` → false. Never re-derived from `motion`.
   */
  vehicleType?: VehicleType | null;
  state?: VehicleState;
  speeding?: boolean;
  lastMovingCourse?: number | null;
  positionAgeSec?: number | null;
}

export interface AdminLiveResponse {
  vehicles: AdminLiveVehicle[];
  serverTime: string;
  /** TRUE means Redis could not be read — NOT that nothing is moving. */
  degraded: boolean;
}

export const adminLiveQueryKey = ['admin', 'live'] as const;

/**
 * 🔴 POLLED, NOT SOCKETED, AND THAT IS THE DESIGN.
 *
 * `live/hub.ts` is strictly tenant-bound and its isolation test asserts that a
 * device never appears on a foreign tenant's socket — a staff socket receiving
 * everything is exactly the shape that test forbids. At this fleet size a
 * five-second poll is indistinguishable to a human and leaves the most
 * safety-tested code in the product untouched.
 *
 * `refetchIntervalInBackground` is deliberately OFF: a control-room tab left
 * open overnight should stop asking, not keep polling a screen nobody is
 * looking at.
 */
export const LIVE_POLL_MS = 5_000;

export function useAdminLive(): UseQueryResult<AdminLiveResponse> {
  return useQuery({
    queryKey: adminLiveQueryKey,
    queryFn: ({ signal }) => apiJson<AdminLiveResponse>('/api/admin/live', { signal }),
    refetchInterval: LIVE_POLL_MS,
    refetchIntervalInBackground: false,
    // A 403 is a decision, not a hiccup — retrying only delays the answer.
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}

/**
 * Map an admin-live row into the shared `LiveVehicle` shape the marker kit reads
 * (slice 84). ONE adapter, used by both the Global-live map (silhouettes + glide)
 * and the list (the state dot via `markerTone`), so list and map cannot disagree.
 *
 * Each parity field is softened the same way the wire type is optional: an absent
 * `vehicleType` → `other` (the dot), absent `state` → `unknown`, absent `speeding`
 * → false. The fields the marker model requires but admin does not send (tracker
 * health, plan, contact recency) are set to honest neutrals — the kit reads none of
 * them for shape, colour or heading. `stale` is left false so `isStale` falls back
 * to `positionAgeSec` (the same > 120 s rule the tenant uses).
 */
export function adminLiveToMarker(v: AdminLiveVehicle): LiveVehicle {
  return {
    id: v.vehicleId,
    plate: v.plate,
    label: v.label,
    model: null,
    plan: 'track',
    vehicleType: normalizeVehicleType(v.vehicleType),
    status: 'online',
    state: v.state ?? 'unknown',
    lastFixMoving: null,
    speeding: v.speeding ?? false,
    motion: v.motion,
    lat: v.lat,
    lon: v.lon,
    speedKmh: v.speedKmh ?? 0,
    course: v.course,
    lastMovingCourse: v.lastMovingCourse ?? null,
    positionFixedAt: v.fixTime,
    positionAgeSec: v.positionAgeSec ?? null,
    trackerContactAt: null,
    freshnessDiverged: false,
    cacheAgeSec: null,
    cached: true,
    stale: false,
    trackerHealth: null,
    hasTracker: true,
    trackerSilentSince: null,
    // SLICE D2 — on-shift driver is an OWNER-side (tenant) feature. The staff
    // Global-live map never shows it (never mix the business green into the admin
    // indigo), and `/api/admin/live` carries no driver fields, so both are null:
    // the name tag draws the plain pill with no initials circle here.
    driverInitials: null,
    driverName: null,
  };
}
